import { useState } from 'react';
import {
  Droplets,
  Pill,
  Syringe,
  Scale,
  Heart,
  UtensilsCrossed,
  Footprints,
  X,
} from 'lucide-react';

interface QuickAddMenuProps {
  onSelect: (type: string) => void;
  onClose: () => void;
}

export function QuickAddMenu({ onSelect, onClose }: QuickAddMenuProps) {
  const [isOpen, setIsOpen] = useState(true);

  const quickAddOptions = [
    { id: 'glucose', label: 'Glucose', icon: Droplets, color: 'bg-dropsugar-green/100' },
    { id: 'medication', label: 'Medication', icon: Pill, color: 'bg-purple-500' },
    { id: 'insulin', label: 'Insulin', icon: Syringe, color: 'bg-pink-500' },
    { id: 'weight', label: 'Weight', icon: Scale, color: 'bg-green-500' },
    { id: 'blood-pressure', label: 'Blood Pressure', icon: Heart, color: 'bg-red-500' },
    { id: 'calories', label: 'Food', icon: UtensilsCrossed, color: 'bg-orange-500' },
    { id: 'steps', label: 'Steps', icon: Footprints, color: 'bg-cyan-500' },
  ];

  const handleSelect = (type: string) => {
    setIsOpen(false);
    onSelect(type);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
      <div className="bg-white rounded-t-3xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Ready to Log</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-6 text-center">Choose what you want to log next</p>
          <div className="grid grid-cols-3 gap-3">
            {quickAddOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className="flex flex-col items-center justify-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all hover:shadow-md group min-h-[96px]"
                >
                  <div className={`${option.color} p-2.5 rounded-full mb-2 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-900 text-center leading-tight">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
